/* PARTNER NETWORK (supplier side) — who this plant shares live load data with. */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Handshake, Construction, Truck, Trash2, ShieldCheck, AlertTriangle, QrCode, Copy, X, Printer } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { printQrPage } from "@/lib/printQr";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Partners() {
  const [data, setData] = useState(null);
  const [kill, setKill] = useState(null);
  const [qr, setQr] = useState(null);        // { id, name, url }

  const showQr = async (p) => {
    if (qr?.id === p.id) return setQr(null);
    if (!p.pump_company_id) return toast.error("Add them under Pump Companies first — that's what mints their tracker link");
    try {
      const r = await api.post(`/pump-companies/${p.pump_company_id}/ensure-track-token`);
      setQr({ id: p.id, name: p.name, url: `${window.location.origin}/pump/${r.data.track_token}` });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create their link"); }
  };

  const load = useCallback(() => {
    api.get("/partners").then((r) => setData(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Could not load partners"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const disconnect = async () => {
    try {
      await api.delete(`/partners/${kill.id}`);
      toast.success(`${kill.name} disconnected — they can no longer see your loads`);
      setKill(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not disconnect"); }
  };

  const sharing = data?.sharing;
  const partners = data?.partners || [];

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="partners-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-primary-orange">Partner Network</div>
          <h1 className="heading text-3xl sm:text-4xl font-black uppercase">Who sees <span className="text-primary-orange">your loads</span></h1>
          <p className="mono text-sm text-muted-industrial mt-1 max-w-2xl">
            Pump companies and haulage contractors you've connected. Each one sees only the jobs their own
            equipment is booked on — truck, live position, ETA and the time each load was batched. Never your
            customers, your prices or anyone else's work.
          </p>
        </div>
        <div className="mono text-[11px] uppercase tracking-widest" data-testid="partners-sharing-state">
          {sharing && (sharing.active ? (
            <span className="inline-flex items-center gap-1.5 border border-emerald-600 text-emerald-400 px-3 py-2"><ShieldCheck className="w-3.5 h-3.5" /> Sharing active</span>
          ) : (
            <span className="inline-flex items-center gap-1.5 border border-amber-500 text-amber-300 px-3 py-2"><AlertTriangle className="w-3.5 h-3.5" /> Sharing paused — upgrade to resume</span>
          ))}
          {sharing?.ends_at && sharing.active && (
            <div className="text-zinc-500 mt-1 normal-case tracking-normal">Free sharing until {new Date(sharing.ends_at).toLocaleDateString()}</div>
          )}
        </div>
      </div>

      {partners.length === 0 ? (
        <div className="border border-dashed border-industrial p-10 text-center" data-testid="partners-empty">
          <Handshake className="w-10 h-10 mx-auto text-primary-orange" />
          <div className="heading text-xl font-black uppercase mt-3">No partners connected</div>
          <p className="mono text-sm text-muted-industrial mt-2 max-w-xl mx-auto">
            A pump company or contractor sends you an invite link from their own ArmorBatch partner account.
            Open it, accept, and they'll see live ETAs for the jobs they're on — which means fewer phone calls
            asking where the truck is.
          </p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="partners-list">
          {partners.map((p) => {
            const Icon = p.kind === "pump" ? Construction : Truck;
            return (
              <div key={p.id} className="card-industrial p-4 flex flex-col gap-2" data-testid={`partner-card-${p.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-primary-orange flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-black" /></div>
                  <div className="min-w-0">
                    <div className="heading text-lg font-black uppercase truncate">{p.name}</div>
                    <div className="mono text-[10px] uppercase tracking-widest text-primary-orange">{p.kind === "pump" ? "Pump company" : "Haulage contractor"}</div>
                  </div>
                  {p.kind === "pump" && (
                    <button type="button" data-testid={`partner-qr-${p.id}`} onClick={() => showQr(p)} title="Owner's tracker link + QR"
                      className="ml-auto p-2 border border-primary-orange text-primary-orange hover:bg-primary-orange hover:text-black"><QrCode className="w-3.5 h-3.5" /></button>
                  )}
                  <button type="button" onClick={() => setKill(p)} data-testid={`partner-disconnect-${p.id}`} title="Disconnect"
                    className={`${p.kind === "pump" ? "" : "ml-auto "}p-2 border border-industrial text-muted-industrial hover:border-red-500 hover:text-red-400`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                <div className="mono text-[11px] text-muted-industrial space-y-0.5">
                  {p.contact_name && <div>{p.contact_name}</div>}
                  <div className="truncate">{p.email}</div>
                  {String(p.phone || "").trim() && <div>{p.phone}</div>}
                  {p.other_suppliers > 0 && <div className="text-zinc-500">Also works with {p.other_suppliers} other supplier{p.other_suppliers === 1 ? "" : "s"}</div>}
                  {p.kind === "pump" && (
                    <div className="text-zinc-500">{p.pump_company_id ? "Bookable on jobs as a pump company" : "Not yet bookable — add them under Pump Companies"}</div>
                  )}
                </div>
                {qr?.id === p.id && (
                  <div className="border border-primary-orange p-3 space-y-2" data-testid={`partner-qr-panel-${p.id}`}>
                    <div className="flex gap-3">
                      <div className="p-2 bg-white self-start"><QRCodeSVG id={`partner-qr-svg-${p.id}`} value={qr.url} size={120} level="M" /></div>
                      <div className="min-w-0 flex-1">
                        <div className="mono text-[10px] uppercase tracking-widest text-primary-orange">Owner's tracker</div>
                        <p className="mono text-[11px] text-muted-industrial mt-1">
                          {qr.name} sees every job their pumps are on. Each individual pump gets its own QR from
                          their portal under <span className="text-ink">My Pumps</span>.
                        </p>
                        <div className="mono text-[10px] break-all border border-industrial px-2 py-1 mt-2" data-testid={`partner-qr-url-${p.id}`}>{qr.url}</div>
                      </div>
                      <button type="button" data-testid={`partner-qr-close-${p.id}`} onClick={() => setQr(null)} className="self-start p-1 text-muted-industrial hover:text-ink"><X className="w-4 h-4" /></button>
                    </div>
                    <div className="flex gap-2">
                      <button type="button" data-testid={`partner-qr-copy-${p.id}`} onClick={() => { navigator.clipboard?.writeText(qr.url); toast.success("Copied"); }}
                        className="mono text-[10px] uppercase tracking-widest border border-industrial px-3 py-1.5 hover:border-primary-orange hover:text-primary-orange inline-flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
                      <button type="button" data-testid={`partner-qr-print-${p.id}`}
                        onClick={() => printQrPage({ title: `Pump Tracker · ${qr.name}`, tagLine: "ARMORBATCH GLOBAL · PUMP TRACKER", heading: qr.name,
                          description: "Scan with your phone camera to see every concrete truck heading to your pours today — live GPS and ETAs.",
                          footer: "© ARMORBATCH GLOBAL · Live pump tracker", url: qr.url, svgElementId: `partner-qr-svg-${p.id}` })}
                        className="mono text-[10px] uppercase tracking-widest border border-industrial px-3 py-1.5 hover:border-primary-orange hover:text-primary-orange inline-flex items-center gap-1"><Printer className="w-3 h-3" /> Print</button>
                      {p.email && (
                        <a href={`mailto:${p.email}?subject=${encodeURIComponent("Your live pump tracker")}&body=${encodeURIComponent(`Here's your permanent pump tracker: ${qr.url}`)}`}
                          data-testid={`partner-qr-email-${p.id}`}
                          className="mono text-[10px] uppercase tracking-widest border border-industrial px-3 py-1.5 hover:border-primary-orange hover:text-primary-orange">Email</a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!kill} onOpenChange={(o) => !o && setKill(null)}>
        <AlertDialogContent className="rounded-none bg-surface border-industrial" data-testid="partner-disconnect-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="heading uppercase text-red-400">Disconnect {kill?.name}?</AlertDialogTitle>
            <AlertDialogDescription className="mono text-sm text-ink">
              They lose all live visibility of your loads immediately. Your jobs and dockets are untouched.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none mono uppercase tracking-widest text-xs" data-testid="partner-disconnect-cancel">Keep connected</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); disconnect(); }} className="rounded-none mono uppercase tracking-widest text-xs bg-red-600 text-white hover:bg-red-700" data-testid="partner-disconnect-confirm">Disconnect</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
